import * as Sentry from "@sentry/react";
import { Suspense, type FC, useMemo } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import InlineLoading from "./components/layouts/loading/InlineLoading";
import OverlayLoading from "./components/layouts/loading/OverlayLoading";
import useCurrentUser from "./hooks/authentication/useCurrentUser";
import createRoutes from "./routing/routes";

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

/**
 * Builds the user-dependent browser router and renders it behind a suspense fallback.
 *
 * Preconditions: Authentication and service providers are mounted by the application entry point.
 * Assumptions: A changed user reference rebuilds the router; this component does not preserve
 * router identity across that change. Suspense handles suspended rendering, not all failures.
 */
const App: FC = () => {
    const user = useCurrentUser();

    const router = useMemo(() => sentryCreateBrowserRouter(createRoutes(user)), [user]);

    return (
        <Suspense fallback={<OverlayLoading text="Loading..." />}>
            <RouterProvider router={router} />
        </Suspense>
    );
};

export default App;
